import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { STORAGE_KEYS } from 'constants/storageKeys';
import { authService } from 'services/authService';
import { collaborationService } from 'services/collaborationService';
import initPushNotifications, { resetPushNotifications } from 'services/pushNotifications';
import socket from 'services/socket';
import { isMasterRole } from 'util/access';
import { storage } from 'util/storage';
import { tokenManager } from 'util/tokenManager';

export const AuthContext = createContext(null);

/** Pull a token out of the various shapes an auth API may return. */
// How often a signed-in tab re-asks the backend what it may do, so a role or
// permission change an admin makes reaches an open panel without a re-login.
const ACCESS_REVALIDATE_MS = 60_000;

const extractToken = (data) =>
  data?.token || data?.accessToken || data?.access_token || null;

/**
 * Demo helper: infer a role from the email local-part so signing in with
 * different addresses (superadmin@…, mentor@…, jury@…) shows the matching
 * dashboard/sidebar. The real backend response's role takes precedence.
 */
const guessRoleFromEmail = (email) => {
  const local = String(email || '').split('@')[0].toLowerCase();
  if (local.includes('super') || local.includes('master')) return 'superadmin';
  if (local.includes('center')) return 'center_admin';
  if (local.includes('mentor')) return 'mentor';
  if (local.includes('jury')) return 'jury_member';
  if (local.includes('founder') || local.includes('startup')) return 'startup_founder';
  return 'center_admin';
};

// A per-center assignment type ('mentor'/'jury') → the effective panel role the
// nav + route guards key on. Jury routes are gated on 'jury_member'.
export const assignmentToRole = (assignment) => {
  const v = String(assignment || '').toLowerCase();
  if (v === 'jury') return 'jury_member';
  return v; // 'mentor'
};

/** Build a normalised user object, always with a display name. */
const buildUser = (data, fallbackEmail) => {
  const raw = data?.user || data?.data || data?.profile || null;
  const email = raw?.email || fallbackEmail || '';
  const name =
    raw?.name ||
    [raw?.firstName, raw?.lastName].filter(Boolean).join(' ') ||
    email ||
    'User';
  // Multi-role support: a user (e.g. mentor + jury) carries all held assignment
  // types plus the currently-active one. The login body is flat; the profile
  // fetch nests under `raw`.
  const assignments = data?.assignments ?? raw?.assignments ?? [];
  // The other multi-role axis: several RBAC ROLES under one email, whose
  // access is the union of all of them (BE membership_role_service). `role`
  // below stays the PRIMARY — route guards and panel selection key on one
  // role — while `roles` is the full set, for display.
  const roleNames =
    data?.role_names ?? raw?.role_names ?? raw?.roles ?? data?.roles ?? [];
  const activeAssignment =
    data?.active_assignment ?? raw?.active_assignment ?? data?.activeAssignment ?? raw?.activeAssignment ?? null;
  // Backend login returns the role at `role_name` (flat), profile at `role`.
  // When an active mentor/jury assignment is present, it decides the panel.
  const baseRole = raw?.role || data?.role || data?.role_name || guessRoleFromEmail(email);
  // Prefer an active mentor/jury assignment; otherwise the base role. Either way
  // normalize `jury` → `jury_member` (route guards key on jury_member).
  const role = assignmentToRole(
    ['mentor', 'jury'].includes(String(activeAssignment).toLowerCase()) ? activeAssignment : baseRole,
  );
  // Login returns the id flat (`user_id`); the profile fetch returns it nested
  // (`raw.id`) — check both so pages needing the numeric id work right after
  // login too, not only after the post-mount profile refresh.
  const id = raw?.id ?? data?.user_id ?? data?.id ?? null;
  // Backend returns this flag flat on login (`must_change_password`) or flat on the
  // profile fetch (`mustChangePassword`, present on `raw` since refreshUser passes
  // `{ user: profile }`) — check both shapes.
  const mustChangePassword = Boolean(
    raw?.mustChangePassword ?? data?.must_change_password ?? data?.mustChangePassword ?? false,
  );
  // Center/org scoping: login returns these FLAT (`center_id`), the profile
  // fetch nested under `raw` in camelCase (`centerId`). Capture both so the
  // stored user always carries its center right after login — center-scoped
  // actions (e.g. Add Stakeholder → getValidCenterId) then work without having
  // to wait for the post-mount profile refresh (which is skipped for local
  // sessions and silently swallows failures).
  const centerId =
    raw?.centerId ?? raw?.center_id ?? data?.center_id ?? data?.centerId ?? null;
  const organizationId =
    raw?.organizationId ?? raw?.organization_id ?? data?.organization_id ?? data?.organizationId ?? null;
  return {
    ...(raw || {}),
    id, name, email, role, mustChangePassword, centerId, organizationId,
    assignments: Array.isArray(assignments) ? assignments : [],
    // Always a list of display names; falls back to the primary alone for any
    // response shape that predates multi-role.
    roles: Array.isArray(roleNames) && roleNames.length
      ? roleNames
      : [role].filter(Boolean),
    activeAssignment,
  };
};

/**
 * Owns authentication state (token, user, role) and exposes login/logout.
 * `token` is held in state so guards react synchronously to sign-in/out.
 */
export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => tokenManager.getToken());
  const [user, setUser] = useState(() => storage.get(STORAGE_KEYS.USER));
  const [permissions, setPermissions] = useState(() => storage.get(STORAGE_KEYS.PERMISSIONS));
  // Additive collaboration axis: the active record-scoped grants this user holds
  // ([{ resource, record_id, permission }]). Never baked into the JWT — live
  // fetched, so a revoked grant disappears on the next refresh. Does NOT feed
  // `can()`; it backs the separate `canCollaborate()` gate below.
  const [collaborations, setCollaborations] = useState([]);
  const [isInitialising, setIsInitialising] = useState(Boolean(tokenManager.getToken()));

  // Resource×{read,write} matrix from the real backend RBAC endpoint
  // (`GET /auth/me/permissions`). Best-effort — a local demo session or a
  // flaky network just leaves the UI ungated rather than blocking login.
  const loadPermissions = useCallback(() => {
    return authService
      .getResourcePermissions()
      .then((response) => {
        const data = response?.data || response;
        if (data?.permissions) {
          storage.set(STORAGE_KEYS.PERMISSIONS, data);
          // Keep the SAME object when nothing moved: this also runs on a timer
          // (see the revalidation effect below), and a fresh identity every
          // minute would re-run every effect that depends on `permissions`.
          setPermissions((prev) =>
            (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
        }
      })
      .catch(() => {});
  }, []);

  // Best-effort load of the caller's active collaboration grants. Like
  // loadPermissions: a local/offline session just leaves the list empty rather
  // than blocking anything.
  const loadCollaborations = useCallback(() => {
    return collaborationService
      .myGrants()
      .then((response) => {
        const data = response?.data ?? response;
        setCollaborations(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, []);

  const persistSession = useCallback(
    (data, fallbackEmail, rememberMe = true) => {
      // Must run before any of the writes below so they land in the right
      // backend: localStorage (survives closing the browser) when remembered,
      // sessionStorage (cleared with the tab/browser) when not.
      storage.setRememberMe(rememberMe);
      const nextToken = extractToken(data) || tokenManager.getToken() || 'local-session';
      const nextUser = buildUser(data, fallbackEmail);
      tokenManager.setToken(nextToken);
      if (data?.refreshToken) tokenManager.setRefreshToken(data.refreshToken);
      storage.set(STORAGE_KEYS.USER, nextUser);
      storage.set(STORAGE_KEYS.ROLE, nextUser.role);
      setToken(nextToken);
      setUser(nextUser);
      if (nextToken !== 'local-session') {
        loadPermissions();
        loadCollaborations();
      }
    },
    [loadPermissions, loadCollaborations],
  );

  const login = useCallback(
    async (credentials) => {
      // Real backend (proxied at /api): POST /auth/login →
      // { success, token, role_name, … }. A real token unlocks the live API.
      const response = await authService.login(credentials);
      if (extractToken(response)) {
        persistSession(response, credentials.email, Boolean(credentials.remember_me));
        // Hydrate the full profile immediately (same as a page reload) so
        // role-scoped data — e.g. the founder dashboard — is present on first
        // render, not only after a manual reload. refreshUser swallows errors.
        await refreshUser();
        // Register FCM as soon as we have a JWT (even on force-password route,
        // which has no NotificationBell). Best-effort; never blocks login.
        initPushNotifications();
        return response;
      }
      // MFA-on accounts get no token here — the backend emailed a login code
      // instead. Surface that so the Login page can switch to an OTP step
      // rather than treating it as a failed sign-in.
      if (response?.mfa_required) {
        return { mfaRequired: true, email: response.email || credentials.email };
      }
      throw new Error(response?.message || 'Invalid email or password');
    },
    // refreshUser is a stable callback (empty deps); omitted intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [persistSession],
  );

  const verifyMfaLogin = useCallback(
    async ({ email, code, rememberMe }) => {
      // Completes the challenge from `login()`: POST /auth/mfa/verify →
      // same token shape as a normal login.
      const response = await authService.mfaVerifyLogin({
        email,
        code,
        remember_me: Boolean(rememberMe),
      });
      if (extractToken(response)) {
        persistSession(response, email, Boolean(rememberMe));
        await refreshUser();
        initPushNotifications();
        return response;
      }
      throw new Error(response?.message || 'Invalid or expired verification code.');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [persistSession],
  );
  // Apply the result of a successful POST /users/set-initial-password — swaps in the
  // refreshed token the backend issues and clears the first-login gate, without a full
  // re-login round-trip.
  const completePasswordReset = useCallback((newToken) => {
    if (newToken) tokenManager.setToken(newToken);
    setUser((prev) => {
      if (!prev) return prev;
      const nextUser = { ...prev, mustChangePassword: false };
      storage.set(STORAGE_KEYS.USER, nextUser);
      return nextUser;
    });
    if (newToken) setToken(newToken);
    initPushNotifications();
  }, []);

  const logout = useCallback(() => {
    resetPushNotifications();
    const current = tokenManager.getToken();
    // Best-effort token revocation for real sessions.
    if (current && current !== 'local-session') {
      authService.logout?.().catch(() => {});
    }
    tokenManager.clear();
    storage.remove(STORAGE_KEYS.PERMISSIONS);
    setToken(null);
    setUser(null);
    setPermissions(null);
    setCollaborations([]);
  }, []);

  // Re-fetch GET /users/profile and refresh the header's `user` (name/email/
  // avatarUrl/role). Callable after any profile save (generic Profile.jsx,
  // or the mentor/jury self-service profile, which syncs its photo back to
  // the same user_profiles.avatar_url column) so the header updates
  // immediately instead of only after a full page reload.
  const refreshUser = useCallback(() => {
    const currentToken = tokenManager.getToken();
    if (!currentToken || currentToken === 'local-session') return Promise.resolve();
    return authService
      .getProfile()
      .then((response) => {
        const profile = response?.data || response;
        if (profile) {
          setUser((prev) => {
            const nextUser = buildUser({ user: profile }, prev?.email);
            storage.set(STORAGE_KEYS.USER, nextUser);
            // The mirrored role key is read outside React (services/rolesEndpoint)
            // — keep it in step, or a primary-role change would only land at the
            // next login.
            if (nextUser.role) storage.set(STORAGE_KEYS.ROLE, nextUser.role);
            // Same reasoning as loadPermissions: unchanged profile, unchanged
            // object, so a periodic revalidation re-renders nothing.
            return JSON.stringify(prev) === JSON.stringify(nextUser) ? prev : nextUser;
          });
        }
      })
      .catch(() => {});
  }, []);

  // Revalidate the session once on load; keep the local session if the profile
  // endpoint is unavailable so a reload doesn't drop the user on the login page.
  useEffect(() => {
    const currentToken = tokenManager.getToken();
    // Skip revalidation for local demo sessions — hitting the protected profile
    // endpoint with a placeholder token would 401 and drop the user to /login.
    // Still resolve `isInitialising` — ProtectedRoute waits on it before
    // evaluating a resource gate, so leaving it stuck at `true` here would hang
    // that guard forever for a demo session.
    if (!currentToken || currentToken === 'local-session') {
      setIsInitialising(false);
      return undefined;
    }
    let active = true;
    initPushNotifications();
    // `isInitialising` only flips false once ALL THREE have settled — not just
    // refreshUser(). ProtectedRoute's resource gate (`can()`/`hasResourceGrant()`)
    // depends on `permissions`/`collaborations`, which used to be fire-and-forget
    // here; a hard reload could resolve `isInitialising` before those arrived,
    // so a collaborator's resource-gated route (e.g. Programs) would see empty
    // grant data and redirect to the dashboard before the real grants loaded.
    Promise.allSettled([loadPermissions(), loadCollaborations(), refreshUser()]).finally(() => {
      if (active) setIsInitialising(false);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // An admin changing this user's roles, panels or permissions must not need
  // them to log out and back in to see it. The backend now answers with their
  // CURRENT access on every request (app/core/dependencies/auth.py re-reads
  // roles and panels from the database), so it's enough to re-ask periodically
  // and whenever the tab comes back to the foreground: the sidebar, the panel
  // name and every `can()` gate are driven by this state, so they re-render on
  // their own. Both loaders keep their previous object when nothing changed,
  // so a quiet tick costs no re-render at all.
  useEffect(() => {
    if (!token || token === 'local-session') return undefined;
    let inFlight = false;
    const revalidate = () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      Promise.allSettled([loadPermissions(), refreshUser()]).finally(() => {
        inFlight = false;
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', onVisibility);
    const timer = setInterval(revalidate, ACCESS_REVALIDATE_MS);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(timer);
    };
  }, [token, loadPermissions, refreshUser]);

  // Cross-tab sync: a profile save in ANOTHER tab writes the new user into
  // localStorage's `authUser` key (see refreshUser above) — the native
  // `storage` event fires here (only in OTHER tabs, never this same tab, and
  // never for sessionStorage-backed "don't remember me" sessions, since
  // those already aren't shared across tabs) so this tab's header/menu pick
  // up the change too, instead of only after this tab's own next reload or
  // profile-page visit.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== STORAGE_KEYS.USER) return;
      refreshUser();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshUser]);

  // Real-time access updates: the backend pushes `access.changed` (after the
  // change commits) when an admin edits this user's permission overrides, role
  // set, or the permissions of a role they hold. Reload at once instead of
  // waiting for the 60s revalidate tick, so the sidebar, route guards and the
  // "No modules" page follow the change immediately.
  useEffect(() => {
    if (!token || token === 'local-session') return undefined;
    return socket.on('access.changed', () => {
      loadPermissions();
      refreshUser();
      // Also reload record-scoped grants so a newly accepted collaboration
      // program/event surfaces its module in the sidebar + list without a reload.
      loadCollaborations();
    });
  }, [token, loadPermissions, refreshUser, loadCollaborations]);

  // Drive the real-time WebSocket off the token: (re)connect whenever a real
  // token appears or changes (login, MFA, panel switch, password reset), and
  // disconnect on logout. `reconnect()` re-reads the current token, so a panel
  // switch reconnects with the fresh JWT. Local demo sessions get no socket.
  useEffect(() => {
    if (token && token !== 'local-session') {
      socket.reconnect();
    } else {
      socket.disconnect();
    }
  }, [token]);

  const role = user?.role ?? storage.get(STORAGE_KEYS.ROLE);

  // Rename-safe authorization principal: the backend's admin SIGNALS rather than
  // the role NAME. `roleIsAdmin` is the role's `is_admin` flag and `scope` is
  // 'organization' | 'center' — both are returned by /users/profile (spread onto
  // `user`) and /auth/me/permissions. `util/access` admin helpers take THIS so a
  // renamed center-admin role keeps every capability. Falls back across the two
  // sources so it's populated right after login and on reload (from stored user).
  const principal = useMemo(
    () => ({
      role,
      roleIsAdmin: user?.roleIsAdmin ?? permissions?.role_is_admin ?? false,
      scope: user?.scope ?? permissions?.scope ?? null,
    }),
    [role, user?.roleIsAdmin, user?.scope, permissions],
  );

  // Resource×action gate backed by `permissions.permissions[resource]`.
  // `write` implies `read`, matching backend semantics.
  //
  // Only the MASTER passes unconditionally. A super admin used to short-circuit
  // here too, which mirrored the backend bypass in `is_center_admin` and made
  // the Super Admin role's permission matrix decorative in the UI: every module
  // stayed in the sidebar however the matrix was ticked. Both sides now agree —
  // a super admin sees exactly the modules their role grants
  // (see app/core/roles.py can_manage_resource).
  const can = useCallback(
    (resource, action = 'read') => {
      if (isMasterRole(role)) return true;
      const entry = permissions?.permissions?.[resource];
      if (!entry) return false;
      return action === 'write' ? Boolean(entry.write) : Boolean(entry.read || entry.write);
    },
    [role, permissions],
  );

  // Record-scoped collaboration gate — the ADDITIVE third axis. Returns true
  // only when the user holds an active grant for exactly this (resource,
  // recordId). Never widens to the whole module; `write` implies `read`,
  // matching the backend. Deliberately separate from `can()` so no existing
  // gate changes behaviour. Call sites OR it with can(): e.g.
  //   can('applications','write') || canCollaborate('applications', id, 'write')
  const canCollaborate = useCallback(
    (resource, recordId, action = 'read') => {
      if (!resource || recordId == null) return false;
      const grant = collaborations.find(
        (c) => c.resource === resource && String(c.record_id) === String(recordId),
      );
      if (!grant) return false;
      return action === 'write' ? grant.permission === 'write' : true;
    },
    [collaborations],
  );

  // True if the user holds any active grant for a module `resource` at the given
  // action level ('read' → any grant; 'write' → a write grant). Drives the
  // record-scoped module visibility: the module's nav item + list/detail route
  // open for a collaborator, and the module then shows only their shared
  // records. Purely additive — never removes access anyone already had.
  const hasResourceGrant = useCallback(
    (resource, action = 'read') => {
      if (!resource) return false;
      // Evaluation Process/Matrix ride on the bound Program's grant — accepting
      // a Program collaboration also opens the Evaluations module (record-scoped
      // Edit is still decided per-item via `canCollaborate('applications', ...)`
      // against the process/matrix's own bound program id). There is no separate
      // `evaluations` grant resource to look up directly.
      const effectiveResource = resource === 'evaluations' ? 'applications' : resource;
      return collaborations.some(
        (c) => c.resource === effectiveResource && (action !== 'write' || c.permission === 'write'),
      );
    },
    [collaborations],
  );

  // True if the user holds an active GROUP ADMIN grant for exactly this
  // (resource, recordId). A group admin may manage the record's collaborators
  // in addition to accessing it — record pages OR this with record ownership to
  // decide whether to render the "Manage Collaborators" surface. Record-scoped;
  // never widens to other records. The backend independently authorises.
  const canCollaborateManage = useCallback(
    (resource, recordId) => {
      if (!resource || recordId == null) return false;
      return collaborations.some(
        (c) =>
          c.resource === resource &&
          String(c.record_id) === String(recordId) &&
          c.role === 'group_admin',
      );
    },
    [collaborations],
  );

  // Switch the active panel for a multi-role user (mentor ↔ jury) in the same
  // center. Hits POST /auth/switch-assignment for a fresh token carrying the new
  // active_assignment, then updates the stored token/role/user so the nav and
  // route guards react — no re-login, same account/password.
  const switchPanel = useCallback(
    async (assignmentType) => {
      const response = await authService.switchAssignment(assignmentType);
      const data = response?.data || response;
      const nextRole = assignmentToRole(data?.active_assignment || assignmentType);
      if (data?.token) {
        tokenManager.setToken(data.token);
        setToken(data.token);
      }
      setUser((prev) => {
        const next = {
          ...(prev || {}),
          role: nextRole,
          activeAssignment: data?.active_assignment ?? assignmentType,
          assignments: data?.assignments ?? prev?.assignments ?? [],
        };
        storage.set(STORAGE_KEYS.USER, next);
        storage.set(STORAGE_KEYS.ROLE, nextRole);
        return next;
      });
      loadPermissions();
      initPushNotifications();
      return nextRole;
    },
    [loadPermissions],
  );

  // Super-admin "Switch Center": re-mint a scoped token (POST /auth/switch-context).
  // `payload` is `{ center_id }` to enter a specific center or `{ organization_id }`
  // to return to organization scope (all assigned centers). After swapping the
  // token we do a FULL page reload of the CURRENT page (not a redirect to
  // /dashboard) — e.g. switching center while on Events stays on Events: every
  // module re-fetches under the new context, so there is no stale or cached
  // data from the previously selected center, and the websocket / permissions
  // / push-notification layers re-bootstrap cleanly, without losing the page
  // the admin was actually working on.
  const switchCenter = useCallback(async (payload) => {
    const response = await authService.switchCenter(payload);
    const data = response?.data || response;
    if (data?.token) {
      tokenManager.setToken(data.token);
      setToken(data.token);
    }
    window.location.reload();
  }, []);

  const value = useMemo(
    () => ({
      user,
      role,
      principal,
      permissions,
      can,
      collaborations,
      canCollaborate,
      canCollaborateManage,
      hasResourceGrant,
      refreshCollaborations: loadCollaborations,
      isAuthenticated: Boolean(token),
      isInitialising,
      login,
      verifyMfaLogin,
      logout,
      refreshUser,
      completePasswordReset,
      switchPanel,
      switchCenter,
    }),
    [
      user,
      role,
      principal,
      permissions,
      can,
      collaborations,
      canCollaborate,
      canCollaborateManage,
      hasResourceGrant,
      loadCollaborations,
      token,
      isInitialising,
      login,
      verifyMfaLogin,
      logout,
      refreshUser,
      completePasswordReset,
      switchPanel,
      switchCenter,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
